import React from 'react';
import { Chip, Box } from '@mui/material';

export const standardTableContainerSx = {
    borderRadius: '12px',
    border: '1px solid #eaecf0',
    boxShadow: '0 1px 3px 0 rgba(16, 24, 40, 0.05), 0 1px 2px 0 rgba(16, 24, 40, 0.03)',
    backgroundColor: '#ffffff',
    overflowX: 'auto',
    '&:has(tbody tr:only-child td[colspan]) .MuiTablePagination-root': {
        display: 'none !important',
    },
    '&:has(tbody:empty) .MuiTablePagination-root': {
        display: 'none !important',
    },
};

export const standardTableHeadSx = {
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #eaecf0',
    '& th, & .MuiTableCell-head': {
        backgroundColor: '#f8fafc',
        color: '#475467',
        fontWeight: 600,
        fontSize: '0.75rem',
        letterSpacing: '0.02em',
        textTransform: 'none',
        py: 1.5,
        px: 2,
        borderBottom: '1px solid #eaecf0',
        whiteSpace: 'nowrap',
    },
};

export const standardTableRowSx = {
    transition: 'background-color 0.15s ease',
    '&:hover': {
        backgroundColor: '#f8fafc !important',
    },
    '&.Mui-selected': {
        backgroundColor: 'rgba(79, 70, 229, 0.04) !important',
        '&:hover': {
            backgroundColor: 'rgba(79, 70, 229, 0.07) !important',
        },
    },
};

export const standardTableCellSx = {
    py: 1.75,
    px: 2,
    borderColor: '#f2f4f7',
    fontSize: '0.875rem',
    color: '#1e293b',
    verticalAlign: 'middle',
};

const AVATAR_PALETTES = [
    { bg: '#f5f3ff', color: '#7c3aed' }, // purple
    { bg: '#eff6ff', color: '#2563eb' }, // blue
    { bg: '#ecfdf5', color: '#059669' }, // emerald
    { bg: '#fff7ed', color: '#ea580c' }, // orange
    { bg: '#fef2f2', color: '#dc2626' }, // red
    { bg: '#fdf2f8', color: '#db2777' }, // pink
    { bg: '#f0fdf4', color: '#16a34a' }, // green
    { bg: '#eef2ff', color: '#4f46e5' }, // indigo
    { bg: '#f0fdfa', color: '#0d9488' }, // teal
];

export const getAvatarColor = (name = '') => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % AVATAR_PALETTES.length;
    return AVATAR_PALETTES[index];
};

export const getInitials = (name = '') => {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
};

export const getRoleChipSx = (roleName = '') => {
    const r = String(roleName).toLowerCase();
    if (r.includes('reception') || r.includes('front')) {
        return { bgcolor: '#eff8ff', color: '#175cd3', border: '1px solid #b2ddff' };
    }
    if (r.includes('store') || r.includes('inventory') || r.includes('stock')) {
        return { bgcolor: '#fff6ed', color: '#c4320a', border: '1px solid #ffd6ae' };
    }
    if (r.includes('restaurant') || r.includes('kitchen') || r.includes('cook') || r.includes('waiter')) {
        return { bgcolor: '#fef3f2', color: '#b42318', border: '1px solid #fecdca' };
    }
    if (r.includes('super admin')) {
        return { bgcolor: '#f9f5ff', color: '#6941c6', border: '1px solid #e9d7fe' };
    }
    if (r.includes('hotel manager') || r.includes('admin') || r.includes('manager')) {
        return { bgcolor: '#eef4ff', color: '#3538cd', border: '1px solid #c7d7fe' };
    }
    if (r.includes('housekeep') || r.includes('clean')) {
        return { bgcolor: '#fff1f3', color: '#e11d48', border: '1px solid #ffe4e8' };
    }
    return { bgcolor: '#f2f4f7', color: '#344054', border: '1px solid #eaecf0' };
};

export const getStatusChipConfig = (status = '') => {
    const s = String(status).toUpperCase();
    const isSuccess = ['ACTIVE', 'CONFIRMED', 'COMPLETED', 'PAID', 'CLEAN', 'INSPECTED', 'AVAILABLE', 'READY', 'ACCEPTED'].includes(s);
    const isDanger = ['INACTIVE', 'CANCELLED', 'VOIDED', 'DIRTY', 'OUT OF ORDER', 'REJECTED', 'LOCKED', 'EXPIRED', 'FAILED'].includes(s);
    const isWarning = ['PENDING', 'DUE', 'PARTIAL', 'IN PROGRESS', 'AWAITING', 'MAINTENANCE', 'OCCUPIED', 'RESERVED'].includes(s);

    if (isSuccess) {
        return {
            bgcolor: '#ecfdf5',
            color: '#027a48',
            border: '1px solid #a6f4c5',
            dotColor: '#12b76a',
            label: status || 'Active'
        };
    }
    if (isDanger) {
        return {
            bgcolor: '#fef3f2',
            color: '#b42318',
            border: '1px solid #fecdca',
            dotColor: '#f04438',
            label: status || 'Inactive'
        };
    }
    if (isWarning) {
        return {
            bgcolor: '#fffaeb',
            color: '#b54708',
            border: '1px solid #fedf89',
            dotColor: '#f79009',
            label: status || 'Pending'
        };
    }
    return {
        bgcolor: '#f2f4f7',
        color: '#344054',
        border: '1px solid #eaecf0',
        dotColor: '#667085',
        label: status || 'Default'
    };
};

export function StandardStatusChip({ status, size = 'small', sx = {} }) {
    const config = getStatusChipConfig(status);
    return (
        <Chip
            size={size}
            label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box
                        sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: config.dotColor,
                            flexShrink: 0,
                        }}
                    />
                    <span>{config.label}</span>
                </Box>
            }
            sx={{
                bgcolor: config.bgcolor,
                color: config.color,
                border: config.border,
                fontWeight: 500,
                fontSize: '0.75rem',
                height: 24,
                borderRadius: '16px',
                '& .MuiChip-label': {
                    px: 1,
                },
                ...sx
            }}
        />
    );
}

export function StandardRoleChip({ role, size = 'small', sx = {} }) {
    const roleName = typeof role === 'object' && role !== null ? role.name : role;
    const roleStyle = getRoleChipSx(roleName);
    return (
        <Chip
            size={size}
            label={roleName || 'N/A'}
            sx={{
                ...roleStyle,
                fontWeight: 500,
                fontSize: '0.75rem',
                height: 24,
                borderRadius: '16px',
                '& .MuiChip-label': {
                    px: 1.25,
                },
                ...sx
            }}
        />
    );
}

/**
 * Robust helper to extract total items count from diverse API response shapes.
 * Handles both `totalCount` and `total` at root, in pagination, meta, or data envelopes.
 */
export const extractTotalCount = (response, fallbackArray = null) => {
    if (response === undefined || response === null) {
        return Array.isArray(fallbackArray) ? fallbackArray.length : 0;
    }
    if (typeof response === 'number' && !isNaN(response)) {
        return response;
    }
    const pagination = response.pagination || (response.data && response.data.pagination) || {};
    const meta = response.meta || (response.data && response.data.meta) || {};
    const candidates = [
        response.totalCount,
        response.total,
        response.total_count,
        response.totalRecords,
        response.totalItems,
        pagination.totalCount,
        pagination.total,
        pagination.total_count,
        pagination.totalRecords,
        pagination.totalItems,
        meta.totalCount,
        meta.total,
        meta.total_count,
        meta.totalRecords,
        meta.totalItems,
        response.data?.totalCount,
        response.data?.total,
        response.data?.total_count,
        response.data?.totalRecords,
        response.data?.totalItems,
        response.count,
        response.data?.count,
    ];
    for (const val of candidates) {
        if (typeof val === 'number' && !isNaN(val)) return val;
        if (typeof val === 'string' && val.trim() !== '' && !isNaN(Number(val))) return Number(val);
    }
    if (Array.isArray(fallbackArray)) return fallbackArray.length;
    if (Array.isArray(response.data)) return response.data.length;
    if (Array.isArray(response.items)) return response.items.length;
    if (Array.isArray(response.guests)) return response.guests.length;
    if (Array.isArray(response)) return response.length;
    return 0;
};

/**
 * Standardized Filter & Button styles matching PMS reference design
 */
export const standardPrimaryButtonSx = {
    backgroundColor: 'var(--primary-color, #4146e0)',
    color: '#ffffff',
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    minHeight: 38,
    px: 2.25,
    boxShadow: 'none',
    '&:hover': {
        backgroundColor: 'var(--primary-dark, #3539bc)',
        boxShadow: 'none',
    },
    '&.Mui-disabled': {
        backgroundColor: '#e2e8f0',
        color: '#94a3b8',
    },
};

export const standardSecondaryButtonSx = {
    backgroundColor: '#ffffff',
    borderColor: '#d0d5dd',
    borderWidth: '1px',
    borderStyle: 'solid',
    color: '#344054',
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    minHeight: 38,
    px: 2,
    boxShadow: 'none',
    '&:hover': {
        backgroundColor: '#f8fafc',
        borderColor: '#cbd5e1',
        color: '#1e293b',
    },
    '& .MuiButton-startIcon': {
        color: '#475467',
    },
    '& .MuiButton-endIcon': {
        color: '#475467',
    },
    '&.Mui-disabled': {
        backgroundColor: '#ffffff',
        borderColor: '#e2e8f0',
        color: '#94a3b8',
        '& .MuiButton-startIcon': {
            color: '#94a3b8',
        },
    },
};

export const standardExportButtonSx = standardSecondaryButtonSx;
export const standardImportButtonSx = standardSecondaryButtonSx;

export const standardFilterContainerSx = {
    backgroundColor: '#ffffff',
    border: '1px solid #edf1f6',
    borderRadius: '12px',
    p: '14px 16px',
    mb: 3,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 2,
};

export const standardSearchInputSx = {
    width: { xs: '100%', sm: 280, md: 320 },
    backgroundColor: '#ffffff',
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        backgroundColor: '#ffffff',
        height: 38,
        fontSize: '0.875rem',
        '& fieldset': {
            borderColor: '#edf1f6',
        },
        '&:hover fieldset': {
            borderColor: '#d0d5dd',
        },
        '&.Mui-focused fieldset': {
            borderColor: 'var(--primary-color, #4146e0)',
            borderWidth: '1.5px',
        },
    },
    '& .MuiInputBase-input::placeholder': {
        color: '#98a0aa',
        opacity: 1,
    },
};

export const standardFilterSelectSx = {
    height: 38,
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    fontSize: '0.875rem',
    color: '#314154',
    fontWeight: 500,
    '& .MuiSelect-select': {
        py: 0,
        height: 38,
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#edf1f6',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#d0d5dd',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--primary-color, #4146e0)',
        borderWidth: '1.5px',
    },
    '& .MuiSelect-icon': {
        color: '#98a0aa',
    },
};

export const standardFilterInputSx = {
    height: 38,
    backgroundColor: '#ffffff',
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        backgroundColor: '#ffffff',
        height: 38,
        fontSize: '0.875rem',
        color: '#314154',
        '& fieldset': {
            borderColor: '#edf1f6',
        },
        '&:hover fieldset': {
            borderColor: '#d0d5dd',
        },
        '&.Mui-focused fieldset': {
            borderColor: 'var(--primary-color, #4146e0)',
            borderWidth: '1.5px',
        },
    },
    '& .MuiInputLabel-root': {
        fontSize: '0.8125rem',
        color: '#64748b',
        transform: 'translate(14px, 9px) scale(1)',
        '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -8px) scale(0.75)',
            backgroundColor: '#ffffff',
            px: 0.5,
        },
    },
    '& .MuiInputBase-input': {
        py: 0,
        height: 38,
        boxSizing: 'border-box',
        fontSize: '0.875rem',
        color: '#314154',
        '&::placeholder': {
            color: '#98a0aa',
            opacity: 1,
        },
    },
};

export const standardResetButtonSx = {
    minHeight: 38,
    height: 38,
    borderRadius: '8px',
    borderColor: '#d0d5dd',
    borderWidth: '1px',
    borderStyle: 'solid',
    color: '#64748b',
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    px: 1.75,
    backgroundColor: '#ffffff',
    boxShadow: 'none',
    '& .MuiButton-startIcon': {
        color: '#64748b',
    },
    '&:hover': {
        borderColor: '#cbd5e1',
        backgroundColor: '#f8fafc',
        color: '#344054',
        boxShadow: 'none',
    },
};

export const standardRefreshButtonSx = {
    width: 38,
    height: 38,
    borderRadius: '8px',
    border: '1px solid #edf1f6',
    backgroundColor: '#ffffff',
    color: '#64748b',
    flexShrink: 0,
    p: 0,
    '&:hover': {
        backgroundColor: '#f8fafc',
        borderColor: '#d0d5dd',
        color: '#314154',
    },
};

/**
 * Standardized Tabs styles matching application reference design
 */
export const standardTabsContainerSx = {
    borderBottom: '1px solid #eaecf0',
    mb: 3,
};

export const standardTabsSx = {
    minHeight: 44,
    '& .MuiTabs-indicator': {
        backgroundColor: 'var(--primary-color, #4146e0)',
        height: 2.5,
        borderRadius: '2px 2px 0 0',
    },
    '& .MuiTab-root': {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.875rem',
        minWidth: 'auto',
        minHeight: 44,
        px: 2.5,
        py: 1.5,
        color: '#64748b',
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        '&.Mui-selected': {
            color: 'var(--primary-color, #4146e0)',
            fontWeight: 600,
        },
        '&:hover': {
            color: '#1e293b',
        },
    },
};

/**
 * Standardized Dashboard Card styles matching application reference design
 */
export const standardDashboardCardSx = {
    borderRadius: '12px',
    border: '1px solid #eaecf0',
    boxShadow: 'none',
    backgroundColor: '#ffffff',
    p: 2.5,
};

export const standardKpiCardSx = {
    borderRadius: '12px',
    border: '1px solid #eaecf0',
    boxShadow: 'none',
    backgroundColor: '#ffffff',
    p: 2.5,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    transition: 'all 0.15s ease',
    '&:hover': {
        borderColor: '#d0d5dd',
    },
};



